import { ReactNode, useMemo } from 'react'

import { ColumnConfig, EmptyStateConfig, PortfolioSection } from './PortfolioSection'

type CountPlacement = 'subtitle' | 'subtitleTop'

interface SectionCountConfig {
  filteredCount: number
  totalCount: number
  label: string
  placement?: CountPlacement
}

interface SectionEmptyConfig {
  state?: EmptyStateConfig
  filteredMessage?: string
  hideStateWhenFiltered?: boolean
}

interface PortfolioDataSectionProps {
  title: string
  children: ReactNode
  columns?: ColumnConfig[]
  mobileColumns?: ColumnConfig[]
  totalValue?: number
  isLoading?: boolean
  isEmpty?: boolean
  filter?: string
  countConfig?: SectionCountConfig
  subtitle?: string | ReactNode
  subtitleTop?: string | ReactNode
  subtitleHighlight?: boolean
  emptyConfig?: SectionEmptyConfig
  emptyComponent?: ReactNode
  gridTemplateColumns?: string
  enableHorizontalScroll?: boolean
  showTableHeader?: boolean
  headerActions?: ReactNode
  mobileHeaderActions?: ReactNode
  footer?: ReactNode
  containerClassName?: string
  headerClassName?: string
  keepMobileContainer?: boolean
  positionCount?: number
}

function buildCountText({ filteredCount, totalCount, label }: SectionCountConfig, hasFilter: boolean): string {
  return hasFilter ? `${filteredCount} of ${totalCount} ${label}` : `${totalCount} ${label}`
}

export function PortfolioDataSection({
  title,
  children,
  columns,
  mobileColumns,
  totalValue,
  isLoading,
  isEmpty,
  filter,
  countConfig,
  subtitle,
  subtitleTop,
  subtitleHighlight,
  emptyConfig,
  emptyComponent,
  gridTemplateColumns,
  enableHorizontalScroll = true,
  showTableHeader,
  headerActions,
  mobileHeaderActions,
  footer,
  containerClassName,
  headerClassName,
  keepMobileContainer,
  positionCount,
}: PortfolioDataSectionProps) {
  const hasFilter = !!filter
  const countPlacement = countConfig?.placement ?? 'subtitleTop'

  const computedCountText = useMemo(() => {
    if (!countConfig) return undefined
    return buildCountText(countConfig, hasFilter)
  }, [countConfig, hasFilter])

  const computedSubtitle = countPlacement === 'subtitle' ? computedCountText : subtitle
  const computedSubtitleTop = countPlacement === 'subtitleTop' ? computedCountText : subtitleTop

  const hideStateWhenFiltered = emptyConfig?.hideStateWhenFiltered ?? true
  const emptyState = hasFilter && hideStateWhenFiltered ? undefined : emptyConfig?.state
  const emptyMessage = hasFilter ? emptyConfig?.filteredMessage : undefined

  return (
    <PortfolioSection
      title={title}
      subtitle={computedSubtitle}
      subtitleTop={computedSubtitleTop}
      totalValue={totalValue}
      positionCount={positionCount}
      columns={columns}
      mobileColumns={mobileColumns}
      showTableHeader={showTableHeader}
      gridTemplateColumns={gridTemplateColumns}
      isLoading={isLoading}
      isEmpty={isEmpty}
      emptyMessage={emptyMessage}
      emptyState={emptyState}
      emptyComponent={emptyComponent}
      headerActions={headerActions}
      mobileHeaderActions={mobileHeaderActions}
      footer={footer}
      containerClassName={containerClassName}
      headerClassName={headerClassName}
      keepMobileContainer={keepMobileContainer}
      enableHorizontalScroll={enableHorizontalScroll}
      subtitleHighlight={subtitleHighlight}
    >
      {children}
    </PortfolioSection>
  )
}
